
import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DeliveryCountdownProps {
  dueDate: string | null;
  isCompleted?: boolean;
}

export function DeliveryCountdown({ dueDate, isCompleted }: DeliveryCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [status, setStatus] = useState<"ok" | "warning" | "overdue" | "completed">("ok");

  useEffect(() => {
    if (!dueDate) {
      setTimeRemaining("");
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const due = new Date(dueDate);
      const diff = due.getTime() - now.getTime();

      if (isCompleted) {
        setTimeRemaining("Concluído");
        setStatus("completed");
        return;
      }

      if (diff <= 0) {
        setTimeRemaining("Atrasado");
        setStatus("overdue");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      let timeText = "";
      if (days > 0) {
        timeText += `${days}d `;
      }
      if (hours > 0 || days > 0) {
        timeText += `${hours}h `;
      }
      timeText += `${minutes}m`;

      setTimeRemaining(timeText);

      // Set status based on time remaining
      if (days === 0 && hours < 24) {
        setStatus("warning");
      } else {
        setStatus("ok");
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [dueDate, isCompleted]);

  if (!dueDate || !timeRemaining) return null;

  const getStatusColor = () => {
    switch (status) {
      case "completed":
        return "bg-green-500 text-white";
      case "overdue":
        return "bg-red-500 text-white";
      case "warning":
        return "bg-amber-500 text-white";
      default:
        return "bg-blue-500 text-white";
    }
  };

  return (
    <Badge className={`flex items-center gap-1 ${getStatusColor()}`}>
      <Clock className="h-3 w-3" />
      <span className="text-xs font-medium">{timeRemaining}</span>
    </Badge>
  );
}
